'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { useParams, useRouter, useSearchParams } from 'next/navigation'

import {
  SearchParameterKeysTypeEnum,
  SearchParameterTypeEnum,
  VersionEnum,
} from '@lib/enums/search.enum'
import { useDebounce } from '@lib/hooks/use-debounce'
import { slugify } from '@lib/hooks/use-slugify'
import { useMedusaSearchParametersStore } from '@lib/store/medusa-search/medusa-search-parameters.store'
import { useMedusaSearchStore } from '@lib/store/medusa-search/medusa-search.store'
import { ArrowLeftIcon } from '@modules/common/icons'
import {
  GoogleMap,
  Marker,
  MarkerClusterer,
  useJsApiLoader,
} from '@react-google-maps/api'
import BubbleCard from 'components/BubbleCard'
import ExperienceCard from 'components/ExperienceCard'
import { useTranslations } from 'next-intl'

import customCluster from '../../public/cluster.svg'
import filter from '../../public/filter.svg'
import mapMarker from '../../public/map-marker.svg'
import search from '../../public/search.svg'
import {
  applyUserSelectionToStore,
  getSelectedValuesForKey,
  renderParameter,
  SwitchParameter,
} from './FilterPopup'

const FilterPopup = dynamic(() => import('./FilterPopup'), {
  ssr: false,
})

const defaultCenter = {
  lat: 48.8566,
  lng: 2.3522,
}

type MapBounds = {
  north: number
  south: number
  east: number
  west: number
}

type MapSnapshot = {
  center: { lat: number; lng: number }
  zoom: number
  bounds: MapBounds
}

function serializeBounds(bounds: MapBounds): string {
  // Format simple et stable, facile à parser côté backend si besoin.
  return `${bounds.north};${bounds.south};${bounds.east};${bounds.west}`
}

function haversineMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number }
): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180
  const R = 6371000
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const lat1 = toRad(a.lat)
  const lat2 = toRad(b.lat)

  const sinDLat = Math.sin(dLat / 2)
  const sinDLng = Math.sin(dLng / 2)
  const h =
    sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng
  return 2 * R * Math.asin(Math.sqrt(h))
}

function boundsAreaApprox(bounds: MapBounds): number {
  const width = Math.max(0, bounds.east - bounds.west)
  const height = Math.max(0, bounds.north - bounds.south)
  return width * height
}

function isMeaningfulChange(prev: MapSnapshot, next: MapSnapshot): boolean {
  const centerMovedMeters = haversineMeters(prev.center, next.center)
  const zoomDelta = Math.abs(prev.zoom - next.zoom)

  const prevArea = boundsAreaApprox(prev.bounds)
  const nextArea = boundsAreaApprox(next.bounds)
  const areaRatio = prevArea > 0 ? Math.abs(nextArea - prevArea) / prevArea : 1

  // “déplacement notable” : seuils conservateurs pour éviter les reloads au moindre pixel.
  // Ajustables si besoin.
  return centerMovedMeters >= 2000 || zoomDelta >= 1 || areaRatio >= 0.2
}

function MapLoader({
  markers,
  mapCenter,
  searchBubble,
  setMapBounds,
  setMapMoved,
}: {
  markers: Array<{ lat: number; lng: number; title?: string; handle?: any }>
  mapCenter: { lat: number; lng: number }
  searchBubble: (marker: any) => void
  setMapBounds: (b: MapBounds) => void
  setMapMoved: (m: boolean) => void
}) {
  const mapRef = useRef<google.maps.Map | null>(null)
  const hasInitializedRef = useRef(false)
  const userGestureRef = useRef(false)
  const lastReportedRef = useRef<MapSnapshot | null>(null)

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY!,
    language: 'fr',
    region: 'FR',
  })

  const onLoad = (map: google.maps.Map) => {
    mapRef.current = map
  }

  const readSnapshot = (): MapSnapshot | null => {
    const map = mapRef.current
    if (!map) return null
    const bounds = map.getBounds()
    const center = map.getCenter()
    const zoom = map.getZoom()
    if (!bounds || !center || typeof zoom !== 'number') return null

    return {
      center: { lat: center.lat(), lng: center.lng() },
      zoom,
      bounds: {
        north: bounds.getNorthEast().lat(),
        south: bounds.getSouthWest().lat(),
        east: bounds.getNorthEast().lng(),
        west: bounds.getSouthWest().lng(),
      },
    }
  }

  const onIdle = () => {
    const snap = readSnapshot()
    if (!snap) return

    // 1) Ignore l'initialisation (évite un reload au premier rendu / recentrage)
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true
      lastReportedRef.current = snap
      userGestureRef.current = false
      return
    }

    // 2) Ignore les déplacements non-utilisateur (ex: updates de props / re-render)
    if (!userGestureRef.current) {
      return
    }

    const prev = lastReportedRef.current
    if (!prev) {
      lastReportedRef.current = snap
      userGestureRef.current = false
      return
    }

    // 3) Ne déclenche que si c'est un vrai “gros” mouvement
    if (!isMeaningfulChange(prev, snap)) {
      userGestureRef.current = false
      return
    }

    lastReportedRef.current = snap
    userGestureRef.current = false
    setMapMoved(true)
    setMapBounds(snap.bounds)
  }

  if (!isLoaded) return <p>Loading</p>

  return (
    <GoogleMap
      mapContainerStyle={{
        height: '100%',
      }}
      options={{
        //mapId: '2342d5707c8734a35edebe12' ,
        mapId: '1cb8efb2afa25f81',
      }}
      center={mapCenter}
      zoom={6}
      onLoad={onLoad}
      onDragStart={() => {
        userGestureRef.current = true
      }}
      onZoomChanged={() => {
        // Le zoom est typiquement un geste utilisateur; on ne triggera qu'à onIdle.
        userGestureRef.current = true
      }}
      onIdle={onIdle}
    >
      <MarkerClusterer
        options={{
          styles: [
            {
              url: customCluster.src,
              height: 54,
              width: 54,
              textColor: '#fff',
              textSize: 15,
            },
          ],
        }}
      >
        {(clusterer) => (
          <>
            {markers.map((marker) => (
              <Marker
                key={marker.handle}
                clusterer={clusterer}
                position={{ lat: marker.lat, lng: marker.lng }}
                title={marker.title}
                icon={{
                  url: mapMarker.src,
                  scaledSize: new window.google.maps.Size(60, 60),
                }}
                onClick={() => {
                  searchBubble(marker)
                }}
              />
            ))}
          </>
        )}
      </MarkerClusterer>
    </GoogleMap>
  )
}

export default function SearchPageComponent({ user_token }) {
  const t = useTranslations('SearchPage')

  const inputRef = useRef<HTMLInputElement | null>(null)
  const resetBtnRef = useRef<HTMLDivElement | null>(null)
  const searchParams = useSearchParams()
  const { countryCode } = useParams() as {
    countryCode: string
  }

  const standalone = searchParams.get('standalone')
  const [bubbleParams, setBubbleParams] = useState(searchParams.get('bulle'))

  const [showMap, setShowMap] = useState(false)
  const [scopeMarker, setScopeMarker] = useState<any>(null)
  const [isBootstrapped, setIsBootstrapped] = useState(false)
  const [showFilterPopup, setShowFilterPopup] = useState(false)
  const [query, setQuery] = useState(searchParams.get('q') ?? '')
  const initialQueryRef = useRef<string>(searchParams.get('q') ?? '')
  const [switchValue, setSwitchValue] = useState<
    VersionEnum.V1 | VersionEnum.V2 | VersionEnum.BV
  >(
    searchParams.get('bulle') !== null
      ? VersionEnum.BV
      : standalone === 'false' || searchParams.get('experiences') === 'true'
        ? VersionEnum.V1
        : VersionEnum.V2
  )
  const [mapBounds, setMapBounds] = useState<MapBounds | null>(null)
  const [mapMoved, setMapMoved] = useState(false)
  const debouncedQuery = useDebounce(query, 1000)
  const debouncedMapBounds = useDebounce(mapBounds, 1000)
  const router = useRouter()

  const {
    fetchMedusaSearch,
    searchResults: experiences,
    searchCategory: categories,
    isLoadingSearchResults: loading,
  } = useMedusaSearchStore()

  const {
    searchParameters,
    currentUserSearchParameters,
    setCurrentUserSearchParameter,
    removeCurrentUserSearchParameter,
    fetchSearchParameters,
    clearCurrentUserSearchParameters,
  } = useMedusaSearchParametersStore()

  const updateSearchParams = useCallback(
    (value: string) => {
      if (!value || value.trim() === '') {
        removeCurrentUserSearchParameter(
          SearchParameterKeysTypeEnum.SEARCH_TEXT
        )
        return
      }

      removeCurrentUserSearchParameter(SearchParameterKeysTypeEnum.SEARCH_TEXT)
      setCurrentUserSearchParameter(
        {
          search_text: {
            type: SearchParameterTypeEnum.TEXT,
            values: [{ key: value, label: value }],
          },
        },
        value,
        true
      )
    },
    [removeCurrentUserSearchParameter, setCurrentUserSearchParameter]
  )

  const initializeSearchParameters = useCallback(async () => {
    await fetchSearchParameters(countryCode)
    applyUserSelectionToStore()
  }, [fetchSearchParameters, countryCode])

  useEffect(() => {
    initializeSearchParameters()
  }, [initializeSearchParameters])

  useEffect(() => {
    updateSearchParams(debouncedQuery)
  }, [debouncedQuery, updateSearchParams])

  useEffect(() => {
    if (initialQueryRef.current) updateSearchParams(initialQueryRef.current)
    setIsBootstrapped(true)
  }, [updateSearchParams])

  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString())
    if (switchValue === VersionEnum.BV) {
      setBubbleParams((prev) => prev ?? 'false')
      params.set('bulle', 'true')
      params.delete('standalone')
    } else if (switchValue === VersionEnum.V2) {
      setBubbleParams(null)
      params.delete('bulle')
      params.set('standalone', 'true')
    } else {
      setBubbleParams(null)
      params.delete('bulle')
      params.delete('standalone')
    }
    router.replace(`?${params.toString()}`, { scroll: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [switchValue])

  useEffect(() => {
    if (debouncedMapBounds && mapMoved) {
      setMapMoved(false)
      const zoneValue = serializeBounds(debouncedMapBounds)
      removeCurrentUserSearchParameter(SearchParameterKeysTypeEnum.WHERE_ZONE)
      setCurrentUserSearchParameter(
        {
          zone: {
            type: SearchParameterTypeEnum.ARRAY,
            values: [{ key: zoneValue, label: zoneValue }],
          },
        },
        zoneValue,
        true
      )
    }
  }, [
    debouncedMapBounds,
    mapMoved,
    removeCurrentUserSearchParameter,
    setCurrentUserSearchParameter,
  ])

  useEffect(() => {
    if (!isBootstrapped) return
    fetchMedusaSearch(countryCode, currentUserSearchParameters)
  }, [
    isBootstrapped,
    currentUserSearchParameters,
    fetchMedusaSearch,
    countryCode,
  ])

  const groupFromUrl = searchParams.get('group')
  useEffect(() => {
    if (!groupFromUrl) return
    clearCurrentUserSearchParameters()
    setCurrentUserSearchParameter(
      {
        group: {
          type: SearchParameterTypeEnum.TEXT,
          values: [{ key: groupFromUrl, label: groupFromUrl }],
        },
      },
      groupFromUrl,
      true
    )
  }, [
    groupFromUrl,
    clearCurrentUserSearchParameters,
    setCurrentUserSearchParameter,
  ])

  const regionFromUrl = searchParams.get('region')
  useEffect(() => {
    if (!regionFromUrl) return
    clearCurrentUserSearchParameters()
    setCurrentUserSearchParameter(
      {
        region: {
          type: SearchParameterTypeEnum.TEXT,
          values: [{ key: regionFromUrl, label: regionFromUrl }],
        },
      },
      regionFromUrl,
      true
    )
  }, [
    regionFromUrl,
    clearCurrentUserSearchParameters,
    setCurrentUserSearchParameter,
  ])

  const categoryFromUrl = searchParams.get('category')
  useEffect(() => {
    if (!categoryFromUrl) return
    clearCurrentUserSearchParameters()
    setCurrentUserSearchParameter(
      {
        category: {
          type: SearchParameterTypeEnum.TEXT,
          values: [{ key: categoryFromUrl, label: categoryFromUrl }],
        },
      },
      categoryFromUrl,
      true
    )
  }, [
    categoryFromUrl,
    clearCurrentUserSearchParameters,
    setCurrentUserSearchParameter,
  ])

  const filteredExperiences = useMemo(() => {
    return experiences.filter((exp) => {
      const isPurchasableWithoutRental =
        exp.metadata?.purchasable_without_rental === true ||
        exp.metadata?.purchasable_without_rental === 'true'

      if (switchValue === VersionEnum.V1) {
        return exp.metadata?.v1 === true || isPurchasableWithoutRental
      } else {
        return exp.metadata?.v1 !== true && !isPurchasableWithoutRental
      }
    })
  }, [experiences, switchValue])

  const searchBubble = (marker) => {
    if (scopeMarker && marker.handle === scopeMarker.handle) {
      clearCurrentUserSearchParameters()
      setScopeMarker(null)
    } else {
      clearCurrentUserSearchParameters()
      setCurrentUserSearchParameter(
        {
          category: {
            type: SearchParameterTypeEnum.TEXT,
            values: [{ key: marker.handle, label: marker.handle }],
          },
        },
        marker.handle,
        true
      )
      setScopeMarker(marker)
    }
  }

  const handleChipToggle = (parameterKey: string, valueKey: string) => {
    const selected = getSelectedValuesForKey(
      parameterKey,
      currentUserSearchParameters
    ).includes(valueKey)
    const param = { [parameterKey]: searchParameters[parameterKey] }
    setCurrentUserSearchParameter(param, valueKey, !selected)
  }

  const SwitchParameterV1 = () => (
    <SwitchParameter
      values={[
        {
          key: VersionEnum.V2,
          label: t('standaloneStations'),
          selected: switchValue === VersionEnum.V2,
        },
        {
          key: VersionEnum.V1,
          label: t('experiences'),
          selected: switchValue === VersionEnum.V1,
        },
        {
          key: VersionEnum.BV,
          label: t('bulles_vertes'),
          selected: switchValue === VersionEnum.BV,
        },
      ]}
      onChange={(key) =>
        setSwitchValue(key as VersionEnum.BV | VersionEnum.V2 | VersionEnum.V1)
      }
    />
  )

  const markers = useMemo(() => {
    return categories
      .map((exp) => {
        const lat = Number(exp.metadata?.gps?.lat)
        const lng = Number(exp.metadata?.gps?.lng)
        if (!lat || !lng) return null
        return {
          lat,
          lng,
          title: exp.name || exp.handle,
          handle: exp.metadata?.strapi_document_id,
        }
      })
      .filter(Boolean)
  }, [categories])

  const selectedCategoryHandle = useMemo(() => {
    const categoryParam = currentUserSearchParameters.find(
      (p) => Object.keys(p)[0] === 'category'
    )
    return categoryParam?.category?.values?.[0]?.key ?? null
  }, [currentUserSearchParameters])

  useEffect(() => {
    if (!selectedCategoryHandle) {
      setScopeMarker(null)
      return
    }

    const marker = markers.find(
      (m) => String(m.handle) === String(selectedCategoryHandle)
    )

    if (!marker) return

    setScopeMarker((prev) => {
      if (prev?.handle === marker.handle && prev?.title === marker.title) {
        return prev
      }
      return marker
    })
  }, [selectedCategoryHandle, markers])

  // Empêche le recentrage automatique: on fixe un centre une seule fois
  // (au moment où on obtient les premiers marqueurs), puis on ne bouge plus.
  const [lockedMapCenter, setLockedMapCenter] = useState(defaultCenter)
  const hasLockedMapCenterRef = useRef(false)

  useEffect(() => {
    hasLockedMapCenterRef.current = false
    setLockedMapCenter(defaultCenter)
  }, [countryCode])

  useEffect(() => {
    if (hasLockedMapCenterRef.current) return
    if (markers.length === 0) return
    const first = markers[0]
    setLockedMapCenter({ lat: first.lat, lng: first.lng })
    hasLockedMapCenterRef.current = true
  }, [markers])

  const mapCenter = lockedMapCenter

  return (
    <div className="flex h-[100vh] w-full flex-col">
      <div className="flex w-full flex-col gap-4 px-6 pb-3 pt-10">
        <div className="flex w-full flex-wrap items-center justify-center gap-4">
          <div className="flex flex-row items-center gap-2">
            <div
              className="flex cursor-text items-center gap-3 rounded-full border py-3 pl-5 pr-6 shadow-md"
              onClick={() => inputRef.current?.focus()}
            >
              <Image src={search} width={30} height={30} alt="search" />
              <input
                ref={inputRef}
                type="text"
                className="text outline-none medium:min-w-[275px] large:min-w-[400px]"
                placeholder={t('searchExperience')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <div
              className="flex cursor-pointer items-center gap-3 rounded-full border px-5 py-3 shadow-md min-[442px]:hidden large:hidden"
              onClick={() => setShowFilterPopup(true)}
            >
              <Image src={filter} width={30} height={30} alt="filter" />
            </div>
          </div>

          <div className="hidden min-[1241px]:flex">
            <SwitchParameterV1 />
          </div>

          <div className="flex min-[1241px]:hidden">
            <SwitchParameterV1 />
          </div>

          <div
            className="hidden cursor-pointer items-center gap-3 rounded-full border px-5 py-3 shadow-md min-[442px]:hidden large:flex"
            onClick={() => setShowFilterPopup(true)}
          >
            <p className="font-bold">{t('filters')}</p>
            <Image src={filter} width={30} height={30} alt="filter" />
          </div>

          <div
            className="hidden cursor-pointer font-medium underline large:block"
            ref={resetBtnRef}
            onClick={() => {
              setQuery('')
              clearCurrentUserSearchParameters()
              router.push('/search')
            }}
          >
            {t('reset')}
          </div>
        </div>
        <div className="hidden grid-cols-3 gap-4 large:grid">
          {Object.keys(searchParameters)
            .filter(
              (key) =>
                !searchParameters[key].hide &&
                searchParameters[key].type == SearchParameterTypeEnum.ARRAY
            )
            .map((key, index, array) => (
              <div
                key={key}
                className={`${index !== array.length - 1 ? '' : ''} col-span-1 row-start-1 ${index === 0 ? 'col-start-3' : index === 1 ? 'col-start-1' : 'col-start-2'}`}
              >
                {renderParameter(
                  key,
                  searchParameters,
                  currentUserSearchParameters,
                  removeCurrentUserSearchParameter,
                  setCurrentUserSearchParameter,
                  handleChipToggle
                )}
              </div>
            ))}
        </div>
      </div>

      <div className="fixed bottom-24 left-1/2 z-50 flex -translate-x-1/2 rounded shadow-lg large:hidden">
        <button
          className="rounded-full bg-green px-8 py-3 text-white"
          onClick={() => setShowMap(!showMap)}
        >
          {showMap ? 'Afficher la liste' : 'Afficher la carte'}
        </button>
      </div>

      <div className="flex flex-1 overflow-hidden large:flex-row">
        <div
          className={`h-full w-full overflow-y-auto px-6 pb-4 pt-1 large:block large:w-7/12 ${showMap ? 'hidden' : 'block'
            } large:block`}
        >
          {scopeMarker && (
            <div className="col-span-full mb-4">
              <h2 className="text-[21px] font-semibold">
                <ArrowLeftIcon
                  className="mr-2 inline-block"
                  onClick={() => {
                    searchBubble(scopeMarker)
                    setBubbleParams(searchParams.get('bulle'))
                  }}
                />
              </h2>
              <h2 className="text-[21px] font-semibold">{scopeMarker.title}</h2>
            </div>
          )}
          <div className="grid grid-cols-[repeat(auto-fit,minmax(275px,1fr))] gap-4">
            {loading &&
              Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="h-[275px] w-full animate-pulse rounded-xl bg-gray-200"
                />
              ))}
            {!loading &&
              bubbleParams == null &&
              filteredExperiences.map((exp) => (
                <ExperienceCard
                  key={exp.id}
                  experience={exp}
                  user_token={user_token}
                />
              ))}

            {!loading &&
              bubbleParams !== null &&
              categories.map((cat) => (
                <BubbleCard
                  key={cat.id}
                  bubble={cat}
                  user_token={user_token}
                  clickAction={() => {
                    const documentId = cat.metadata?.strapi_document_id
                    if (!documentId) return
                    if (cat.metadata?.slug) {

                      console.log(`/${countryCode}/bulles-vertes/${slugify(cat.metadata.slug)}`)

                      router.push(
                        `/${countryCode}/bulles-vertes/${slugify(cat.metadata.slug)}`
                      )
                      return
                    } else {
                      console.log(`/${countryCode}/bubble/${slugify(cat.name)}-${documentId}`)
                      router.push(`/${countryCode}/bubble/${slugify(cat.name)}-${documentId}`)

                    }
                  }}
                />
              ))}
          </div>
        </div>

        <div
          className={`w-full overflow-hidden rounded large:block large:w-5/12 ${showMap ? 'block' : 'hidden'
            }`}
        >
          <MapLoader
            key={countryCode}
            markers={markers}
            mapCenter={mapCenter}
            searchBubble={searchBubble}
            setMapBounds={setMapBounds}
            setMapMoved={setMapMoved}
          />
        </div>
      </div>

      {showFilterPopup && (
        <FilterPopup
          resetRef={resetBtnRef}
          onClose={() => setShowFilterPopup(false)}
          onApply={() => {
            setShowFilterPopup(false)
            fetchMedusaSearch(countryCode, currentUserSearchParameters)
          }}
        />
      )}
    </div>
  )
}
